import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  formatEuroCents,
  revenueQuery,
} from '~/features/subscriptions'
import { ScrollableDataRegion } from '~/components/data/ScrollableDataRegion'

/**
 * `/admin/subscriptions/revenue` — Revenue analytics (HANDOFF §9).
 *
 * 8 KPI tiles + MRR breakdown stacked bar (Recharts) + by-plan card +
 * top accounts table. Uses the existing Recharts dep — no new libs.
 */
export const Route = createFileRoute('/admin/subscriptions/revenue')({
  component: RevenuePage,
})

function RevenuePage(): ReactElement {
  const revenue = useQuery(revenueQuery())
  const data = revenue.data

  if (revenue.isLoading) return <Skeleton />
  if (revenue.isError) {
    return (
      <div className="ka-subs-list-page ka-subs-revenue-page">
        <div className="ka-card" role="alert">
          <div className="ka-empty">
            <div className="ka-empty-title">Couldn&rsquo;t load revenue</div>
            <div className="ka-empty-body">
              The revenue endpoint may be unreachable. Refresh to retry.
            </div>
          </div>
        </div>
      </div>
    )
  }
  if (!data) return <Skeleton />

  const tiles: ReadonlyArray<{
    readonly label: string
    readonly value: string
  }> = [
    { label: 'MRR', value: formatEuroCents(data.mrr_cents) },
    { label: 'ARR', value: formatEuroCents(data.arr_cents) },
    {
      label: 'Net new MRR',
      value: formatEuroCents(data.net_new_mrr_cents ?? 0),
    },
    {
      label: 'Expansion MRR',
      value: formatEuroCents(data.expansion_mrr_cents ?? 0),
    },
    {
      label: 'Contraction MRR',
      value: formatEuroCents(data.contraction_mrr_cents ?? 0),
    },
    {
      label: 'Churned MRR',
      value: formatEuroCents(data.churned_mrr_cents ?? 0),
    },
    {
      label: 'Revenue this month',
      value: formatEuroCents(data.revenue_this_month_cents ?? 0),
    },
    {
      label: 'Revenue last month',
      value: formatEuroCents(data.revenue_last_month_cents ?? 0),
    },
  ]

  // Convert cents → euros for the chart so the y-axis ticks render
  // friendly "€ 100" labels instead of cents.
  const series = data.series.map((p) => ({
    period: p.period,
    new: (p.new_cents ?? 0) / 100,
    expansion: (p.expansion_cents ?? 0) / 100,
    reactivation: (p.reactivation_cents ?? 0) / 100,
    contraction: -((p.contraction_cents ?? 0) / 100),
    churn: -((p.churn_cents ?? 0) / 100),
  }))

  return (
    <div className="ka-subs-list-page ka-subs-revenue-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Revenue</h1>
          <p className="ka-page-header-subtitle">
            {formatEuroCents(data.mrr_cents)} MRR ·{' '}
            {formatEuroCents(data.arr_cents)} ARR
          </p>
        </div>
      </header>

      <div
        role="list"
        aria-label="Revenue KPIs"
        className="ka-kpi-strip ka-subs-revenue-kpis ka-page-section"
      >
        {tiles.map((tile) => (
          <div
            key={tile.label}
            role="listitem"
            className="ka-kpi ka-subs-revenue-kpi"
          >
            <div className="ka-kpi-copy">
              <div className="ka-kpi-label">{tile.label}</div>
              <div className="ka-kpi-value">{tile.value}</div>
            </div>
          </div>
        ))}
      </div>

      {series.length > 0 ? (
        <section
          className="ka-card ka-card--flush ka-page-section ka-subs-revenue-card"
          aria-label="MRR breakdown"
        >
          <div className="ka-card-head">
            <span className="ka-card-head-title">MRR breakdown</span>
          </div>
          <div className="ka-chart-wrap">
            <ResponsiveContainer>
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period" stroke="var(--fg-muted)" />
                <YAxis stroke="var(--fg-muted)" />
                <Tooltip />
                <Bar
                  dataKey="new"
                  stackId="a"
                  fill="var(--chip-success-fg, #16a34a)"
                />
                <Bar
                  dataKey="expansion"
                  stackId="a"
                  fill="var(--chip-info-fg, #2563eb)"
                />
                <Bar
                  dataKey="reactivation"
                  stackId="a"
                  fill="var(--primary, #6366f1)"
                />
                <Bar
                  dataKey="contraction"
                  stackId="a"
                  fill="var(--chip-warning-fg, #f59e0b)"
                />
                <Bar
                  dataKey="churn"
                  stackId="a"
                  fill="var(--chip-destructive-fg, #dc2626)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      ) : null}

      {data.by_plan.length > 0 ? (
        <section
          className="ka-card ka-card--flush ka-page-section ka-subs-revenue-card"
          aria-label="Revenue by plan"
        >
          <div className="ka-card-head">
            <span className="ka-card-head-title">Revenue by plan</span>
          </div>
          <ScrollableDataRegion label="Revenue by plan" minWidth="840px">
            <table className="ka-table">
            <thead>
              <tr>
                <th scope="col">Plan</th>
                <th scope="col" className="ka-num">Subscribers</th>
                <th scope="col" className="ka-num">MRR</th>
                <th scope="col" className="ka-num">Δ</th>
              </tr>
            </thead>
            <tbody>
              {data.by_plan.map((p) => (
                <tr key={p.plan_id}>
                  <td>{p.plan_name}</td>
                  <td className="ka-num">{p.subscribers}</td>
                  <td className="ka-num">{formatEuroCents(p.mrr_cents)}</td>
                  <td className="ka-num">
                    {p.delta_cents != null
                      ? formatEuroCents(p.delta_cents)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </ScrollableDataRegion>
        </section>
      ) : null}

      {data.top_accounts.length > 0 ? (
        <section
          className="ka-card ka-card--flush ka-page-section ka-subs-revenue-card"
          aria-label="Top accounts"
        >
          <div className="ka-card-head">
            <span className="ka-card-head-title">Top accounts</span>
          </div>
          <ScrollableDataRegion label="Top revenue accounts" minWidth="840px">
            <table className="ka-table">
            <thead>
              <tr>
                <th scope="col">Org</th>
                <th scope="col">Plan</th>
                <th scope="col" className="ka-num">MRR</th>
              </tr>
            </thead>
            <tbody>
              {data.top_accounts.map((a) => (
                <tr key={a.org_id}>
                  <td>{a.org_name}</td>
                  <td>{a.plan_name}</td>
                  <td className="ka-num">{formatEuroCents(a.mrr_cents)}</td>
                </tr>
              ))}
            </tbody>
            </table>
          </ScrollableDataRegion>
        </section>
      ) : null}
    </div>
  )
}

function Skeleton(): ReactElement {
  return (
    <div className="ka-subs-list-page ka-subs-revenue-page" aria-busy="true">
      <div className="ka-card">
        <div className="ka-card-head">
          <div className="ka-sk ka-sk--label" />
        </div>
        <div className="ka-sk ka-sk--chart" />
      </div>
    </div>
  )
}
